import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const PERIODS = [
  ["today", "Hari Ini"], ["yesterday", "Kemarin"], ["last7", "7 Hari Terakhir"],
  ["month", "Bulan Ini"], ["last_month", "Bulan Sebelumnya"], ["year", "Tahun Ini"],
  ["last_year", "Tahun Sebelumnya"], ["custom", "Kustom"],
];
const rp = (n) => "Rp" + (n == null ? "0" : Math.round(n).toLocaleString("id-ID"));
const pct = (n) => (n == null ? "—" : n + "%");

function StatusBadge({ status }) {
  const map = { FINAL: "bg-emerald-500/15 text-emerald-400", "BELUM FINAL": "bg-amber-500/15 text-amber-400", "DATA TIDAK LENGKAP": "bg-red-500/15 text-red-400" };
  return <span data-testid="report-status" className={`text-xs px-2 py-1 rounded ${map[status] || "bg-slate-500/15 text-slate-300"}`}>{status}</span>;
}

function PeriodBar({ period, setPeriod, start, setStart, end, setEnd, compare, setCompare, onRun }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <div>
        <label className="block text-xs text-slate-400 mb-1">Periode</label>
        <select data-testid="period-select" value={period} onChange={(e) => setPeriod(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white">
          {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {period === "custom" && (
        <>
          <div><label className="block text-xs text-slate-400 mb-1">Dari</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white" /></div>
          <div><label className="block text-xs text-slate-400 mb-1">Sampai</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white" /></div>
        </>
      )}
      {setCompare && (
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" data-testid="compare-toggle" checked={compare} onChange={(e) => setCompare(e.target.checked)} /> Bandingkan periode sebelumnya</label>
      )}
      <Button data-testid="run-report" onClick={onRun} className="bg-indigo-600 hover:bg-indigo-500">Tampilkan</Button>
    </div>
  );
}

function Row({ label, value, prev, bold }) {
  const diff = prev != null && value != null ? value - prev : null;
  return (
    <div className={`flex justify-between py-2 border-b border-slate-800 ${bold ? "font-semibold text-white" : "text-slate-300"}`}>
      <span>{label}</span>
      <span className="flex gap-6">
        <span>{rp(value)}</span>
        {prev != null && <span className="text-slate-500 text-sm w-40 text-right">{rp(prev)} ({diff >= 0 ? "+" : ""}{rp(diff)})</span>}
      </span>
    </div>
  );
}

export default function FinanceReports() {
  const [tab, setTab] = useState("pnl");
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState(""); const [end, setEnd] = useState("");
  const [compare, setCompare] = useState(false);
  const [pnl, setPnl] = useState(null); const [bs, setBs] = useState(null); const [cf, setCf] = useState(null);
  const [maps, setMaps] = useState([]);

  const q = () => `period=${period}${period === "custom" ? `&start_date=${start}&end_date=${end}` : ""}`;

  const load = async () => {
    try {
      if (tab === "pnl") setPnl((await api.get(`/finance/profit-loss?${q()}&compare=${compare}`)).data);
      if (tab === "bs") setBs((await api.get(`/finance/balance-sheet?${q()}`)).data);
      if (tab === "cf") setCf((await api.get(`/finance/cash-flow?${q()}`)).data);
      if (tab === "map") setMaps((await api.get(`/finance/cashflow-mapping`)).data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const p = pnl?.previous;

  return (
    <div className="p-6" data-testid="finance-reports-page">
      <h1 className="text-2xl font-semibold text-white mb-1">Laporan Keuangan</h1>
      <p className="text-slate-400 text-sm mb-4">Berbasis jurnal yang sudah Posted (accrual). Nilai Draft tidak termasuk.</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pnl" data-testid="tab-pnl">Laba Rugi</TabsTrigger>
          <TabsTrigger value="bs" data-testid="tab-bs">Neraca</TabsTrigger>
          <TabsTrigger value="cf" data-testid="tab-cf">Arus Kas</TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map">Mapping Arus Kas</TabsTrigger>
        </TabsList>

        <TabsContent value="pnl">
          <PeriodBar {...{ period, setPeriod, start, setStart, end, setEnd, compare, setCompare, onRun: load }} />
          {pnl && (
            <Card className="p-6 bg-slate-900 border-slate-800">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-400 text-sm">{pnl.period.from} s/d {pnl.period.to}</span>
                <StatusBadge status={pnl.status} />
              </div>
              {pnl.warning && <div data-testid="hpp-warning" className="mb-4 p-3 rounded bg-amber-500/10 text-amber-400 text-sm">
                ⚠ {pnl.warning} ({pnl.hpp_incomplete.orders} order, {pnl.hpp_incomplete.sku_count} SKU, omzet {rp(pnl.hpp_incomplete.revenue_affected)}) — <a href="/costing/waiting" className="underline">Transaksi Menunggu HPP</a></div>}
              <Row label="Total Pendapatan" value={pnl.revenue} prev={p?.revenue} bold />
              <Row label="Harga Pokok Penjualan (HPP)" value={pnl.cogs} prev={p?.cogs} />
              <Row label="Laba Kotor" value={pnl.gross_profit} prev={p?.gross_profit} bold />
              <div className="flex justify-between py-1 text-slate-500 text-sm"><span>Gross Margin</span><span>{pct(pnl.gross_margin)}</span></div>
              <Row label="Biaya Marketplace" value={pnl.marketplace_fee} />
              <Row label="Total Beban Operasional" value={pnl.operating_expense} prev={p?.operating_expense} />
              <Row label="Laba Operasional" value={pnl.operating_profit} prev={p?.operating_profit} bold />
              <Row label="Pendapatan/Beban Lain" value={pnl.other_income_expense} />
              <Row label="Laba Bersih" value={pnl.net_profit} prev={p?.net_profit} bold />
              <div className="flex justify-between py-1 text-slate-500 text-sm"><span>Net Margin</span><span>{pct(pnl.net_margin)}</span></div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="bs">
          <PeriodBar {...{ period, setPeriod, start, setStart, end, setEnd, onRun: load }} />
          {bs && (
            <Card className="p-6 bg-slate-900 border-slate-800">
              <div className="flex justify-between items-center mb-4"><span className="text-slate-400 text-sm">Per {bs.as_of}</span>
                <span data-testid="bs-balance" className={`text-xs px-2 py-1 rounded ${bs.balanced ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{bs.balanced ? "SEIMBANG" : "NERACA TIDAK SEIMBANG"}</span></div>
              <h3 className="text-white font-semibold mt-2">ASET</h3>
              {[...bs.assets.current, ...bs.assets.non_current].map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} value={a.amount} />)}
              <Row label="TOTAL ASET" value={bs.assets.total} bold />
              <h3 className="text-white font-semibold mt-4">LIABILITAS</h3>
              {bs.liabilities.current.map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} value={a.amount} />)}
              <Row label="TOTAL LIABILITAS" value={bs.liabilities.total} bold />
              <h3 className="text-white font-semibold mt-4">EKUITAS</h3>
              {bs.equity.lines.map((a) => <Row key={a.code} label={`${a.code} · ${a.name}`} value={a.amount} />)}
              <Row label="TOTAL EKUITAS" value={bs.equity.total} bold />
              <Row label="TOTAL LIABILITAS + EKUITAS" value={bs.total_liabilities_equity} bold />
              <div className="flex justify-between py-2 mt-2"><span className="text-slate-300">Selisih Neraca</span><span className={bs.balanced ? "text-emerald-400" : "text-red-400"}>{rp(bs.selisih_neraca)}</span></div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="cf">
          <PeriodBar {...{ period, setPeriod, start, setStart, end, setEnd, onRun: load }} />
          {cf && (
            <Card className="p-6 bg-slate-900 border-slate-800">
              <div className="text-slate-400 text-sm mb-4">{cf.period.from} s/d {cf.period.to}</div>
              <Row label="Saldo Kas Awal" value={cf.saldo_kas_awal} />
              <Row label="Arus Kas Operasi" value={cf.operasi} />
              <Row label="Arus Kas Investasi" value={cf.investasi} />
              <Row label="Arus Kas Pendanaan" value={cf.pendanaan} />
              <Row label="Kenaikan/Penurunan Bersih Kas" value={cf.kenaikan_bersih} bold />
              <Row label="Saldo Kas Akhir" value={cf.saldo_kas_akhir} bold />
              <div className="flex justify-between py-1 text-slate-500 text-sm"><span>Saldo Kas (Buku Besar)</span><span>{rp(cf.saldo_kas_gl)} · selisih {rp(cf.selisih)}</span></div>
              {cf.warning && <div className="mt-3 p-3 rounded bg-amber-500/10 text-amber-400 text-sm">⚠ {cf.warning}</div>}
              {cf.belum_dipetakan.length > 0 && <div className="mt-2 text-sm text-slate-400">Belum Dipetakan: {cf.belum_dipetakan.map((u) => `${u.account_code} (${rp(u.amount)})`).join(", ")}</div>}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="map">
          <Card className="p-6 bg-slate-900 border-slate-800">
            <h3 className="text-white font-semibold mb-3">Mapping Arus Kas (Akun → Kategori)</h3>
            <table className="w-full text-sm text-slate-300"><thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="py-2">Akun</th><th>Kategori</th><th>Status</th><th>Efektif</th></tr></thead>
              <tbody>{maps.map((m) => <tr key={m.account_code} className="border-b border-slate-800"><td className="py-2">{m.account_code}</td><td>{m.category}</td><td>{m.status}</td><td>{m.effective_date}</td></tr>)}</tbody></table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
